/**
 * Twin join-approval prompt (restricted rooms). The backend dispatches Mochi
 * (the assistant's meeting twin) into a LiveKit room and, when the room is
 * RESTRICTED, publishes a data message on topic "twin" instead of letting the
 * twin join unattended:
 *
 *   { event: "twin.request", payload: { dispatch_id, twin_name, twin_of } }
 *
 * `shouldShowTwinPrompt` is the pure decision — kept out of the
 * `useDataChannel` listener so it's unit-testable without a live LiveKit
 * room. It returns true only when the decoded message is a real
 * "twin.request" event AND the local participant is allowed to admit
 * (the same `may_admit_guests` signal InCallView already uses for the
 * waiting-room gate).
 */

export const TWIN_REQUEST_EVENT = "twin.request";

export interface TwinRequestPayload {
  dispatch_id?: unknown;
  twin_name?: unknown;
  twin_of?: unknown;
}

export interface TwinDataMessage {
  event?: unknown;
  payload?: unknown;
}

/** Decode a "twin" topic data-channel payload as JSON; null for anything
 *  that isn't a well-formed JSON object (defensive — never throw on a
 *  malformed/foreign packet). */
export function parseTwinDataMessage(
  decoder: TextDecoder,
  payload: Uint8Array,
): TwinDataMessage | null {
  try {
    const data: unknown = JSON.parse(decoder.decode(payload));
    return data && typeof data === "object" ? (data as TwinDataMessage) : null;
  } catch {
    return null;
  }
}

/** True only when `message` is a real twin.request event AND the local
 *  participant may admit (eligible to approve/deny). */
export function shouldShowTwinPrompt(
  message: TwinDataMessage | null | undefined,
  canAdmit: boolean,
): boolean {
  return Boolean(message && message.event === TWIN_REQUEST_EVENT && canAdmit);
}

/** Extracts a usable {dispatch_id, twin_name} pair from a twin.request
 *  message's payload, or null if the required fields aren't present. */
export function extractTwinRequest(
  message: TwinDataMessage,
): { dispatchId: string; twinName: string } | null {
  const payload = message.payload as TwinRequestPayload | undefined;
  const dispatchId = payload?.dispatch_id;
  const twinName = payload?.twin_name;
  if (typeof dispatchId !== "string" || dispatchId.length === 0) return null;
  return {
    dispatchId,
    twinName: typeof twinName === "string" && twinName.length > 0 ? twinName : "Mochi",
  };
}
